import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useRef, useState } from 'react';
import { getEPOList, postEPOMovementDirection } from '../../../api/v2/epo';
import { getPortProcesses } from '../../../api/v2/load';
import { getEPOPortDropdown } from '../../apiCalls';
import { GoogleAnalyticsContext } from '../../GoogleAnalytics';
import { Input, Select } from '../../Input/Input';
import { useStepper } from '../../Stepper';
import { StoreContext } from '../../Store';
import cargoShip from '../assets/cargo-ship.png';
import StepWrapper from './StepWrapper';

export function EntryPortPage() {
  const { stepBackward, localData, setDataByKey, activeStepKey, gotoStep } = useStepper();
  const queryClient = useQueryClient();

  const store = useContext(StoreContext);
  const [isHybridPort, setIsHybridPort] = useState(false);

  const portProcessMutation = useMutation({
    mutationFn: () => getPortProcesses(),
  });

  const nextPage = async () => {
    const validationErrors = [];

    if (!localData.entryport?.id) {
      validationErrors.push('Please select a port to continue.');
    }
    if (isHybridPort && !localData.portModal) {
      validationErrors.push('Please select a port model to continue.');
    }

    setError(validationErrors);
    if (validationErrors.length > 0) return;

    const fd = new FormData();
    fd.append('fromAreaCode', localData.fromAreaCode);
    fd.append('toAreaCode', localData.toAreaCode);

    const movementData = await queryClient.fetchQuery({
      queryKey: ['postEPOMovementDirection', localData.fromAreaCode, localData.toAreaCode],
      queryFn: () => postEPOMovementDirection(fd),
      staleTime: Infinity,
    });

    if (movementData) {
      const data = await queryClient.fetchQuery({
        queryKey: ['getEPOList', movementData[0].id],
        queryFn: () => getEPOList(movementData[0].id),
        staleTime: Infinity,
      });

      store.lockDirection[1](true);
      setDataByKey('categoryOptions', data);
      setDataByKey('categoryOptionsDefault', data);
      gotoStep('5. CategoryOfGoods');
    }
  };

  useEffect(() => {
    getEPOPortDropdown('Import', store.epoDirection[0]).then((data) => {
      store.entryPortOptions[1](data);
    });
  }, [store.epoDirection[0]]);

  const previousPage = () => {
    setError([]);
    stepBackward();
  };

  const isInitialized = useRef(false);
  const ga = useContext(GoogleAnalyticsContext);

  useEffect(() => {
    if (ga.gaInitialized) {
      if (activeStepKey === '3. Entry Port' && isInitialized.current === false) {
        isInitialized.current = true;
        window?.gtag?.('event', '3. Entry Port', {
          event_category: 'EPO',
        });
      }
    }
  }, [activeStepKey, ga.gaInitialized]);

  const [error, setError] = useState([]);

  useEffect(() => {
    if (isHybridPort) {
      const fetchData = async () => {
        const data = await portProcessMutation.mutateAsync();
        store.portsModalOptions[1](data);
      };
      fetchData();
    }
  }, [isHybridPort]);

  // show extra fields if selected port is inventory linked (or user has selected inventory linked)
  const showContainerFields = localData?.entryport?.portType === 'IL' || Number(localData?.portModal) === 2;

  // clear container fields if user changes port or port modal
  useEffect(() => {
    setDataByKey('jiraContainer', '');
    setDataByKey('epoUCN', '');
    setDataByKey('epoBOL', '');
  }, [localData?.entryport?.id, localData?.portModal]);

  return (
    <StepWrapper
      stepTitle="Place of Arrival"
      stepText="Place of Arrival"
      stepKey="3. Entry Port"
      handleNextClick={nextPage}
      handleBackClick={previousPage}
      hideUnlessVisited
    >
      <div className="d-flex flex-row step-banner justify-content-center">
        <img src={cargoShip} width="150" height="150" alt="port" className="mt-4 mb-4" />
      </div>

      <div className="d-flex flex-column align-self-center mt-auto ">
        <span className="d-flex align-self-center flex-column  " style={{ maxWidth: '280px', fontWeight: '500' }}>
          Please select the expected arrival
        </span>

        <span className="d-flex align-self-center flex-column  " style={{ maxWidth: '280px', fontWeight: '500' }}>
          location for this freight movement.{' '}
        </span>
      </div>

      <div className="d-flex flex-column my-3 gap-4" style={{ paddingLeft: '6rem', paddingRight: '6rem' }}>
        <Select
          label="Arrival Location"
          name="entryport"
          data={localData.entryport?.id}
          onChangeAsObject={(data) => {
            const hybrid = data?.hybrid === 1;
            setIsHybridPort(hybrid);
            setError([]);

            let modal = '';
            if (!hybrid) {
              if (data?.portType === 'GVMS') modal = 1;
              else if (data?.portType === 'IL') modal = 2;
            }
            setDataByKey('entryport', data);
            setDataByKey('portModal', modal);
          }}
          options={store.entryPortOptions[0]}
          filter={1}
          required
          data-testid="arrivalLocation-dropdown"
        />
        {isHybridPort && (
          <Select
            label="Port Model"
            name="mixedPortsModalentry"
            data={localData.portModal}
            setData={(data) => {
              setDataByKey('portModal', data);
            }}
            options={store.portsModalOptions[0]}
            filter={1}
          />
        )}
        {showContainerFields && (
          <>
            <div
              className="d-flex align-self-center flex-column text-center"
              style={{ maxWidth: '280px', fontWeight: '500' }}
            >
              If available, please provide any of the below references.
            </div>

            <Input
              label="Container Number"
              name="jiraContainer"
              value={localData.jiraContainer}
              onChange={(e) => setDataByKey('jiraContainer', e.target.value)}
              data-testid="inv-link-container"
            />
            <Input
              label="Container UCN"
              name="epoUCN"
              value={localData.epoUCN}
              onChange={(e) => setDataByKey('epoUCN', e.target.value)}
              data-testid="inv-link-ucn"
            />
            <Input
              label="Bill of Lading"
              name="epoBOL"
              value={localData.epoBOL}
              onChange={(e) => setDataByKey('epoBOL', e.target.value)}
              data-testid="inv-link-bol"
            />
          </>
        )}
      </div>
      <div className="px-3 mb-3 w-75 mx-auto">
        {error.length > 0 && (
          <div className="alert-danger px-3 py-1 w-100 text-center" style={{ borderRadius: '10px' }}>
            {error.map((err, index) => (
              <p key={index}>{err}</p>
            ))}
          </div>
        )}
      </div>
    </StepWrapper>
  );
}
